import React, { useEffect, useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { supabase } from '@/integrations/supabase/client';

// =====================================================================
// BlogArticle — single article view. SEO-optimized (Helmet meta tags,
// OG cards, canonical URL, article schema). Content is trusted HTML
// from Rankpill; we dangerouslySetInnerHTML because the source is our
// own webhook (HMAC-verified). If we ever accept user-submitted blog
// content, sanitize with DOMPurify first.
// =====================================================================

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
  });
}

export default function BlogArticle() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setNotFound(false);
      const { data, error } = await supabase
        .from('blog_articles')
        .select('*')
        .eq('slug', slug)
        .eq('status', 'published')
        .maybeSingle();
      if (cancelled) return;
      if (error || !data) {
        setNotFound(true);
        setLoading(false);
        return;
      }
      setArticle(data);
      setLoading(false);

      // Fetch 3 related by category
      if (data.category) {
        const { data: relatedData } = await supabase
          .from('blog_articles')
          .select('id, slug, title, featured_image_url, published_at, reading_time_minutes')
          .eq('status', 'published')
          .eq('category', data.category)
          .neq('id', data.id)
          .order('published_at', { ascending: false })
          .limit(3);
        if (!cancelled) setRelated(relatedData || []);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0e1a] text-slate-400 flex items-center justify-center font-mono">
        Loading article…
      </div>
    );
  }
  if (notFound) return <Navigate to="/blog" replace />;

  const canonicalUrl = `https://www.snap-chart.ai/blog/${article.slug}`;
  const ogImage = article.featured_image_url || 'https://www.snap-chart.ai/og-default.png';

  // JSON-LD Article schema — helps Google surface with rich snippets.
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.meta_description || article.excerpt,
    image: [ogImage],
    datePublished: article.published_at,
    dateModified: article.updated_at,
    author: { '@type': 'Organization', name: article.author || 'SnapChart AI' },
    publisher: {
      '@type': 'Organization',
      name: 'SnapChart AI',
      logo: { '@type': 'ImageObject', url: 'https://www.snap-chart.ai/logo.png' },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl },
  };

  return (
    <>
      <Helmet>
        <title>{article.meta_title || article.title} — SnapChart AI</title>
        <meta name="description" content={article.meta_description || article.excerpt || ''} />
        <link rel="canonical" href={canonicalUrl} />
        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={article.meta_title || article.title} />
        <meta property="og:description" content={article.meta_description || article.excerpt || ''} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImage} />
        <meta property="article:published_time" content={article.published_at} />
        {article.updated_at && <meta property="article:modified_time" content={article.updated_at} />}
        {article.category && <meta property="article:section" content={article.category} />}
        {Array.isArray(article.keywords) && article.keywords.map(k => (
          <meta key={k} property="article:tag" content={k} />
        ))}
        {/* Twitter card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@JeanMichelNews" />
        <meta name="twitter:title" content={article.meta_title || article.title} />
        <meta name="twitter:description" content={article.meta_description || article.excerpt || ''} />
        <meta name="twitter:image" content={ogImage} />
        {/* JSON-LD */}
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-[#0a0e1a] to-[#14192b] text-white">
        {/* Nav */}
        <div className="border-b border-white/10 bg-black/30 backdrop-blur-sm sticky top-0 z-10">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/blog" className="text-cyan-400 hover:text-cyan-300 text-sm font-mono uppercase tracking-widest">
              ← All articles
            </Link>
            <Link to="/" className="text-white/60 hover:text-white text-sm font-mono">
              snap-chart.ai
            </Link>
          </div>
        </div>

        <article className="max-w-3xl mx-auto px-6 py-12">
          {/* Meta bar */}
          <div className="flex items-center gap-3 text-xs font-mono text-slate-400 mb-4">
            {article.category && (
              <span className="px-2 py-0.5 rounded border border-cyan-500/40 bg-cyan-500/10 text-cyan-300 uppercase tracking-widest">
                {article.category}
              </span>
            )}
            <span>{formatDate(article.published_at)}</span>
            {article.reading_time_minutes && <span>· {article.reading_time_minutes} min read</span>}
            <span>· by {article.author || 'SnapChart AI'}</span>
          </div>

          {/* Title */}
          <h1 className="text-4xl md:text-5xl font-bold leading-tight mb-6 bg-gradient-to-r from-white via-cyan-100 to-white bg-clip-text text-transparent">
            {article.title}
          </h1>

          {/* Excerpt */}
          {article.excerpt && (
            <p className="text-lg text-slate-300 leading-relaxed mb-8">{article.excerpt}</p>
          )}

          {/* E-E-A-T authority callout — original data + expertise signal.
              Google demotes generic AI content on YMYL (finance) topics
              unless it sees "Experience, Expertise, Authority, Trust"
              signals. This block gives all four in one glance. */}
          <div className="mb-8 rounded-xl border border-cyan-500/20 bg-gradient-to-r from-cyan-500/[0.06] to-emerald-500/[0.03] px-5 py-4 flex items-start gap-3">
            <div className="text-2xl leading-none pt-0.5">📊</div>
            <div className="text-sm text-slate-300 leading-relaxed">
              <span className="text-white font-semibold">Built on 10,000+ chart analyses.</span>{' '}
              This article draws on patterns observed while running the SnapChart AI Risk Officer
              on live trader setups every day.{' '}
              <Link to="/register" className="text-cyan-300 hover:text-cyan-200 underline underline-offset-2 whitespace-nowrap">
                See the tool →
              </Link>
            </div>
          </div>

          {/* Hero image */}
          {article.featured_image_url && (
            <div className="mb-10 rounded-2xl overflow-hidden border border-white/10">
              <img
                src={article.featured_image_url}
                alt={article.title}
                className="w-full h-auto"
              />
            </div>
          )}

          {/* Content — Rankpill-generated HTML (trusted source, HMAC-verified).
              Custom CSS below styles the raw HTML since we can't rely on
              @tailwindcss/typography being installed. Keeps the article body
              readable + matches the dark navy/cyan brand. */}
          <style>{`
            .snapchart-article { color: #cbd5e1; font-size: 17px; line-height: 1.75; }
            .snapchart-article > * + * { margin-top: 1.25em; }
            .snapchart-article h1,
            .snapchart-article h2,
            .snapchart-article h3,
            .snapchart-article h4 {
              color: #ffffff; font-weight: 700; line-height: 1.25;
              margin-top: 2em; margin-bottom: 0.6em;
              letter-spacing: -0.01em;
            }
            .snapchart-article h1 { font-size: 2.25rem; }
            .snapchart-article h2 {
              font-size: 1.75rem;
              padding-bottom: 0.4em;
              border-bottom: 1px solid rgba(255,255,255,0.08);
            }
            .snapchart-article h3 { font-size: 1.35rem; color: #67e8f9; }
            .snapchart-article h4 { font-size: 1.1rem; color: #a5f3fc; }
            .snapchart-article p { color: #cbd5e1; }
            .snapchart-article strong { color: #ffffff; font-weight: 600; }
            .snapchart-article em { color: #e0f2fe; }
            .snapchart-article a {
              color: #22d3ee; text-decoration: underline;
              text-underline-offset: 3px; text-decoration-thickness: 1px;
              transition: color 0.15s;
            }
            .snapchart-article a:hover { color: #67e8f9; }
            .snapchart-article ul,
            .snapchart-article ol { padding-left: 1.6em; }
            .snapchart-article ul { list-style-type: disc; }
            .snapchart-article ol { list-style-type: decimal; }
            .snapchart-article ul li,
            .snapchart-article ol li { margin-top: 0.5em; padding-left: 0.3em; }
            .snapchart-article ul li::marker { color: #22d3ee; }
            .snapchart-article ol li::marker { color: #22d3ee; font-weight: 600; }
            .snapchart-article blockquote {
              border-left: 3px solid #22d3ee;
              background: rgba(34,211,238,0.05);
              padding: 1em 1.25em;
              margin-left: 0; margin-right: 0;
              color: #e0f2fe; font-style: italic;
              border-radius: 0 8px 8px 0;
            }
            .snapchart-article code {
              color: #6ee7b7; background: rgba(255,255,255,0.06);
              padding: 0.15em 0.4em; border-radius: 4px;
              font-size: 0.9em;
              font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
            }
            .snapchart-article pre {
              background: rgba(0,0,0,0.5); border: 1px solid rgba(255,255,255,0.1);
              padding: 1.25em; border-radius: 12px; overflow-x: auto;
            }
            .snapchart-article pre code { background: transparent; padding: 0; color: #e2e8f0; }
            .snapchart-article hr {
              border: 0; border-top: 1px solid rgba(255,255,255,0.1);
              margin: 2.5em 0;
            }
            .snapchart-article img {
              max-width: 100%; height: auto;
              border-radius: 12px; border: 1px solid rgba(255,255,255,0.1);
              margin: 1.5em 0;
            }
            .snapchart-article table {
              width: 100%; border-collapse: collapse;
              margin: 1.5em 0;
              background: rgba(255,255,255,0.02);
              border: 1px solid rgba(255,255,255,0.1);
              border-radius: 12px; overflow: hidden;
            }
            .snapchart-article th {
              background: rgba(34,211,238,0.08); color: #ffffff;
              padding: 0.75em 1em; text-align: left; font-weight: 600;
              border-bottom: 1px solid rgba(255,255,255,0.1);
            }
            .snapchart-article td {
              padding: 0.75em 1em; color: #cbd5e1;
              border-bottom: 1px solid rgba(255,255,255,0.05);
            }
            .snapchart-article tr:last-child td { border-bottom: none; }
            .snapchart-article figure { margin: 1.5em 0; }
            .snapchart-article figcaption {
              text-align: center; color: #94a3b8; font-size: 0.9em;
              margin-top: 0.5em; font-style: italic;
            }
          `}</style>
          <div
            className="snapchart-article"
            dangerouslySetInnerHTML={{ __html: article.content_html || '' }}
          />

          {/* Product callout — since we don't have real screenshots wired
              yet, render a designed feature card instead of a broken
              image. Still delivers the "here's what the app does"
              signal that Google reads as Experience/Trust. */}
          <div className="mt-12 rounded-2xl border border-cyan-500/30 bg-gradient-to-br from-cyan-500/[0.08] via-slate-900/40 to-emerald-500/[0.05] p-6 md:p-8">
            <div className="flex items-start gap-4 mb-5">
              <img src="/snap-chart-logo.png" alt="" className="w-12 h-12 flex-shrink-0" />
              <div>
                <div className="text-xs font-mono uppercase tracking-widest text-cyan-400 mb-1">
                  See it live
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-white leading-tight">
                  SnapChart AI's verdict engine — TRADE, NO-TRADE, or WAIT in 10 seconds.
                </h3>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5 text-sm">
              <div className="rounded-lg border border-white/10 bg-black/30 px-3 py-2.5">
                <div className="text-cyan-400 font-mono text-[10px] uppercase tracking-widest mb-1">Any chart</div>
                <div className="text-slate-200">Drag from TradingView, MT4, Binance, prop dashboards</div>
              </div>
              <div className="rounded-lg border border-white/10 bg-black/30 px-3 py-2.5">
                <div className="text-emerald-400 font-mono text-[10px] uppercase tracking-widest mb-1">AI verdict</div>
                <div className="text-slate-200">Setup type + risk score + reasons it might fail</div>
              </div>
              <div className="rounded-lg border border-white/10 bg-black/30 px-3 py-2.5">
                <div className="text-rose-400 font-mono text-[10px] uppercase tracking-widest mb-1">Free</div>
                <div className="text-slate-200">5 analyses / month, no credit card, cancel anytime</div>
              </div>
            </div>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-black font-semibold transition-colors text-sm"
            >
              Try the free verdicts →
            </Link>
          </div>

          {/* Author bio — Google favors named authors with credentials
              on YMYL (finance) content. Static block, applies to every
              article until we support per-author bios. */}
          <div className="mt-10 rounded-2xl border border-white/10 bg-white/[0.02] p-6 flex items-start gap-4">
            <img
              src="/team/jean-michel-portal.jpg"
              alt="Jean-Michel Portal — Founder of SnapChart AI"
              className="w-16 h-16 rounded-full object-cover border-2 border-cyan-500/40 flex-shrink-0"
              loading="lazy"
            />
            <div>
              <div className="text-white font-semibold mb-1">
                Jean-Michel Portal
              </div>
              <div className="text-xs text-cyan-400 font-mono uppercase tracking-widest mb-2">
                Founder · SnapChart AI
              </div>
              <p className="text-sm text-slate-300 leading-relaxed">
                Built SnapChart AI after watching too many prop traders blow up on setups a
                risk officer would have vetoed in seconds. Now I review 10,000+ trader charts
                a month with our AI and share what the data actually says — not what trader
                Twitter wants to hear.{' '}
                <a href="https://x.com/jeanmichelnews" target="_blank" rel="noreferrer noopener"
                   className="text-cyan-300 hover:text-cyan-200 underline underline-offset-2">
                  Follow on X →
                </a>
              </p>
            </div>
          </div>

          {/* CTA — funnel to snap-chart.ai signup */}
          <div className="mt-16 rounded-2xl border border-cyan-500/30 bg-gradient-to-br from-cyan-500/[0.08] to-emerald-500/[0.05] p-8 text-center">
            <div className="text-sm font-mono uppercase tracking-widest text-cyan-400 mb-3">
              Try SnapChart AI free
            </div>
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
              Stop taking bad trades.
            </h3>
            <p className="text-slate-300 mb-6 max-w-xl mx-auto">
              Upload any chart. Get an AI verdict in 10 seconds. 5 free analyses / month, no credit card.
            </p>
            <Link
              to="/register"
              className="inline-flex items-center px-8 py-3 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-black font-semibold transition-colors"
            >
              Get 5 free verdicts →
            </Link>
          </div>

          {/* Related */}
          {related.length > 0 && (
            <div className="mt-16 border-t border-white/10 pt-10">
              <h2 className="text-2xl font-semibold mb-6">More on {article.category}</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {related.map(r => (
                  <Link
                    key={r.id}
                    to={`/blog/${r.slug}`}
                    className="group rounded-lg border border-white/10 bg-white/[0.03] hover:border-cyan-500/40 overflow-hidden"
                  >
                    {r.featured_image_url && (
                      <div className="aspect-video bg-black/40 overflow-hidden">
                        <img src={r.featured_image_url} alt={r.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" />
                      </div>
                    )}
                    <div className="p-4">
                      <div className="text-[10px] font-mono text-slate-500 mb-1">
                        {formatDate(r.published_at)}{r.reading_time_minutes ? ` · ${r.reading_time_minutes} min` : ''}
                      </div>
                      <h4 className="text-sm text-white group-hover:text-cyan-300 line-clamp-2">{r.title}</h4>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </article>
      </div>
    </>
  );
}
